import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Metadata and compatibility conditions for an integration with analysis templates.
 *
 * @package
 */
export interface AnalysisTemplateIntegration {
  name: string;
  icon: BenchlingIconName;
  visible: () => boolean;
  checks: Array<CompatibilityCheckFn>;
}

export enum CheckResultLevel {
  PASS = 'PASS',
  FAIL = 'FAIL',
  WARN = 'WARN',
}

/** @package */
export interface CompatibilityCheckResult {
  level: CheckResultLevel;
  message: string;
}

/**
 * Check a compatibility condition.
 *
 * PASS/FAIL conditions should state the same positive requirement, e.g.: 'Must have no table variables'.
 *
 * WARNINGS should appear if applicable, and should explain the situation with a complete sentence.
 *
 * @returns CompatibilityCheckResult if a message should be shown, else null.
 * @package
 */
export type CompatibilityCheckFn = (variables: TemplateVariablesByType) => CompatibilityCheckResult | null;

/**
 * Basic variable types.
 *
 * `AnalysisTemplateVariable` models are discriminated into `FILE` and `FIELD`, for display purposes.
 *
 * @package
 */
export enum VariableType {
  TABLE = 'TABLE',
  FILE = 'FILE',
  FIELD = 'FIELD',
}

/**
 * User-facing name of the basic variable types.
 *
 * @package
 */
export const BASIC_VARIABLE_TYPE_TO_NAME: Record<VariableType, string> = {
  [VariableType.TABLE]: 'table',
  [VariableType.FILE]: 'file',
  [VariableType.FIELD]: 'field',
};

/**
 * A template's variables grouped by their basic types.
 *
 * @package
 */
export interface TemplateVariablesByType {
  [VariableType.TABLE]: AnalysisTemplateGraphQL['dataFrameVariables'];
  [VariableType.FILE]: AnalysisTemplateGraphQL['configVariables'];
  [VariableType.FIELD]: AnalysisTemplateGraphQL['configVariables'];
}
